import React, { useState } from 'react'
import { X, QrCode, Sparkles, RefreshCw, AlertTriangle, Check } from 'lucide-react'
import { bindQrCode, unbindQrCode } from '../../api/qr'

interface QrBindModalProps {
  isOpen: boolean
  onClose: () => void
  deviceInstanceId: number
  currentCode?: string | null
  currentSerialNumber?: string | null
  onSuccess: (updated: any) => void
}

export default function QrBindModal({
  isOpen,
  onClose,
  deviceInstanceId,
  currentCode,
  currentSerialNumber,
  onSuccess,
}: QrBindModalProps) {
  const [code, setCode] = useState(currentCode || '')
  const [serialNumber, setSerialNumber] = useState(currentSerialNumber || '')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleGenerateRandom = () => {
    // Generate Crockford Base32 5-char code client-side as suggestion
    const chars = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
    let res = ''
    for (let i = 0; i < 5; i++) {
      res += chars[Math.floor(Math.random() * chars.length)]
    }
    setCode(res)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cleanCode = code.trim().toUpperCase()
    if (!cleanCode) {
      setError('Введите код QR-стикера')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const res = await bindQrCode({
        code: cleanCode,
        device_instance_id: deviceInstanceId,
        serial_number: serialNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      })
      onSuccess(res)
      onClose()
    } catch (err: any) {
      console.error('Failed to bind QR code:', err)
      setError(err?.response?.data?.error || err?.message || 'Ошибка привязки QR-кода')
    } finally {
      setLoading(false)
    }
  }

  const handleUnbind = async () => {
    if (!currentCode) return
    if (!window.confirm(`Вы уверены, что хотите отвязать QR-код #${currentCode} от этого устройства?`)) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      await unbindQrCode(currentCode)
      onSuccess(null)
      onClose()
    } catch (err: any) {
      console.error('Failed to unbind QR code:', err)
      setError(err?.response?.data?.error || err?.message || 'Ошибка отвязки QR-кода')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {currentCode ? 'Замена / перепривязка QR-кода' : 'Привязка QR-стикера'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}

          {currentCode && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                Сейчас к устройству привязан стикер{' '}
                <span className="font-mono font-bold">#{currentCode}</span>. При вводе нового кода старый будет
                помечен как «Заменен» с сохранением истории замен.
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Код наклейки / стикера <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="например: A7K9M"
                maxLength={16}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-base uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handleGenerateRandom}
                title="Сгенерировать случайный код"
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-700 flex items-center gap-1 text-xs shrink-0 font-medium"
              >
                <Sparkles className="w-4 h-4 text-blue-600" />
                Автокод
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Буквы A-Z и цифры 0-9. Ссылка будет: <span className="font-mono">e2.bagsus.ru/q/{code || '...'}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Серийный номер устройства (S/N)
            </label>
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="например: SN-2024-88412"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Примечание к замене / привязке
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="например: Первичная маркировка при сборке щита"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="pt-2 flex items-center justify-between border-t border-gray-100">
            {currentCode ? (
              <button
                type="button"
                onClick={handleUnbind}
                disabled={loading}
                className="text-xs text-red-600 hover:text-red-800 font-medium hover:underline"
              >
                Отвязать текущий стикер
              </button>
            ) : <div />}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium shadow-sm transition-colors disabled:opacity-50"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Сохранить
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
